import {AfterViewInit, Component, OnDestroy, OnInit, QueryList, TemplateRef, ViewChild, ViewChildren} from '@angular/core';
import {animate, state, style, transition, trigger} from '@angular/animations';
import {I18NMatPaginatorIntl, I18NMatPaginatorIntlConfig} from '../../shared/i18n-mat-paginator-intl';
import {MatPaginator, MatPaginatorIntl} from '@angular/material/paginator';
import {I18NService} from '../../i18n/i18n.service';
import {BehaviorSubject, combineLatest, forkJoin, merge, Observable, of, Subject} from 'rxjs';
import {Dialog, DialogService} from '../../shared/dialog/dialog.service';
import {
    ECRepresentative,
    ECRepresentativePersonalData,
    ECRepresentativeType,
    EnergyCommunity,
    EntityType,
    NewSecureECRepresentativeRequest,
    UpdateECRepresentativeRequest
} from 'smag-commons/cfg/hierarchy';
import {FormBuilder, FormControl, FormGroup} from '@angular/forms';
import {SmagApiService} from '../../api/smag-api.service';
import {Hierarchy, HierarchyEntity, HierarchyService} from '../../shared/hierarchy.service';
import {MsgBoxService} from '../../shared/msg-box.service';
import {Router} from '@angular/router';
import {catchError, debounceTime, filter, map, startWith, switchMap, takeUntil, tap} from 'rxjs/operators';
import {errorCode, isBlank, isDefined} from '../../commons/commons';
import {SpinnerData} from '../../shared/spinner/spinner.component';
import {MISSING_CHECKS, Permission} from 'smag-commons/dto/api';
import {can} from '../../shared/permissions';
import {ErrorCode} from '../../api/errors';
import {MemberAccount} from '../ec-members/account';
import {NewRepresentativeComponent, NewRepresentativeMember} from './new-representative/new-representative.component';
import {MatDialog} from '@angular/material/dialog';
import {TenantInfo} from '../dev-dashboard/dev-dashboard.component';
import {
    CHAD_DIALOG_MAX_WIDTH,
    CHAD_DIALOG_MIN_WIDTH, DEBOUNCE_MILLIS,
    DEFAULT_DIALOG_MAX_WIDTH, DEFAULT_DIALOG_MIN_WIDTH,
    DEFAULT_PAGE_SIZE,
    DEFAULT_PAGE_SIZES
} from '../../shared/ec-economics';

interface Community {
    entity?: HierarchyEntity; // can be `null` (e.g. if no energy community is selected)
    canView?: boolean;
    canUpdate?: boolean;
    community?: EnergyCommunity;
}

interface Members {
    members: RepresentativeMember[];
    total: number;
}

export interface TenantsAndNetworks {
    tenantId: string;
    networks: HierarchyEntity[];
    freeNetworks?: HierarchyEntity[];
}

export interface RepresentativeMember {
    name: string;
    representative: ECRepresentative;
    account?: MemberAccount;
}

export interface EditedRepresentativeMember { // same DTO as ECRepresentativeRequest
    representativeType: ECRepresentativeType;
    personalData: ECRepresentativePersonalData;
    tenant: string;
    networkIds: string[];
}

enum UpdateReason {
    USER_ACCOUNT_STATE,
    USER_AUTH, // password change, 2FA
    STATE, // state changes, such as "invitation"
    FIELDS // member's "fields", including POD creation/elimination
}

enum SortCriterion {
    CREATED_AT = 'created_at',
    LAST_NAME = 'last_name',
    COMPANY_NAME = 'company_name'
}

enum SortDirection {
    ASC = 'asc',
    DESC = 'desc'
}

const DEFAULT_SORT_CRITERIA: [SortCriterion, SortDirection][] = [
    [SortCriterion.CREATED_AT, SortDirection.DESC],
    [SortCriterion.LAST_NAME, SortDirection.ASC],
    [SortCriterion.COMPANY_NAME, SortDirection.ASC]
];

const SET_GAP = 144;

@Component({
    selector: 'app-ec-representatives',
    templateUrl: './ec-representatives.component.html',
    styleUrls: ['./ec-representatives.component.scss'],
    animations: [
        trigger('detailExpand', [
            state('collapsed', style({height: '0px', minHeight: '0'})),
            state('expanded', style({height: '*'})),
            transition('expanded <=> collapsed', animate('225ms cubic-bezier(0.4, 0.0, 0.2, 1)')),
        ]),
    ],
    providers: [
        {
            provide: I18NMatPaginatorIntlConfig,
            useValue: {itemsPerPageLabel: 'Members per page'} as I18NMatPaginatorIntlConfig
        },
        {provide: MatPaginatorIntl, useClass: I18NMatPaginatorIntl, deps: [I18NService, I18NMatPaginatorIntlConfig]}
    ]
})
export class ECRepresentativesComponent implements OnInit, AfterViewInit, OnDestroy {

    private destroyed = new Subject<void>();
    private reloadMembers: Subject<void>;
    private editedRepresentativeDialogRef: Dialog<any, EditedRepresentativeMember>;
    private memberToEdit: ECRepresentative;
    private searchForm: FormGroup;
    private sortCriteria: [SortCriterion, SortDirection][];
    @ViewChildren('paginator', {emitDistinctChangesOnly: true}) private paginator: QueryList<MatPaginator>;
    @ViewChild('editRepresentative') private editRepresentativeDialog: TemplateRef<any>;
    @ViewChild('deleteRepresentative') private deleteRepresentativeDialog: TemplateRef<any>;
    @ViewChild('disableRepresentative') private disableRepresentativeDialog: TemplateRef<any>;
    @ViewChild('forcePwdChange') private forcePwdChangeDialog: TemplateRef<any>;
    @ViewChild('force2FAReset') private force2FAResetDialog: TemplateRef<any>;
    @ViewChild('spinner') private spinnerDialog: TemplateRef<any>;

    displayedColumns: string[];
    community: BehaviorSubject<Community>;
    communities: HierarchyEntity[];
    hierarchy: Hierarchy;
    members: BehaviorSubject<Members>;
    expandedMember: BehaviorSubject<RepresentativeMember>;
    templateIsDownloading: boolean;
    tenantIds: string[];
    tenants: TenantInfo[];
    singleTenantId: string;
    tenantFilterSubject: Subject<any>;
    tenantsAndNetworks: TenantsAndNetworks[];

    // exported in HTML
    PAGE_SIZE = DEFAULT_PAGE_SIZE;
    PAGE_SIZES = DEFAULT_PAGE_SIZES;
    SortCriterion = SortCriterion;
    SortDirection = SortDirection;

    constructor(private readonly apiService: SmagApiService,
                private readonly hierarchyService: HierarchyService,
                private readonly msgBoxService: MsgBoxService,
                private readonly i18n: I18NService,
                private readonly router: Router,
                private readonly dialog: MatDialog,
                private readonly dialogService: DialogService,
                private readonly fb: FormBuilder) {
        this.reloadMembers = new Subject<void>();
        this.displayedColumns = ['chevron', 'client', 'firstName', 'lastName', 'companyName', 'status', 'registered', 'out', 'actions'];
        this.community = new BehaviorSubject<Community>({entity: null});
        this.communities = null;
        this.members = new BehaviorSubject<Members>(null);
        this.expandedMember = new BehaviorSubject<RepresentativeMember>(null);
        this.searchForm = this.fb.group({
            filters: this.fb.group({
                selectedTenants: this.fb.control(null)
            }),
            textFilter: this.fb.control('')
        });
        this.sortCriteria = [...DEFAULT_SORT_CRITERIA];
        this.templateIsDownloading = false;
        this.tenantIds = [];
        this.singleTenantId = null; // Only defined if we are supervisors in multitenant structure
        this.tenantsAndNetworks = [];
        this.tenants = null;
    }

    ngOnInit() {
        // Fetch both communities and tenants
        combineLatest([
            this.hierarchyService.findAllEntities(entity => entity.type === EntityType.ENERGY_COMMUNITY),
            this.hierarchyService.hierarchy
        ]).pipe(
            takeUntil(this.destroyed))
            .subscribe(([communities, hierarchy]) => {
                this.communities = communities;
                this.hierarchy = hierarchy;
                const tenants = hierarchy.entities.filter(res =>
                    res.type === EntityType.ROOT_NODE && can(res.permissions, Permission.READ));
                this.tenants = [];
                if (can(hierarchy.permissions, Permission.READ)) {
                    // Single tenant or multi-tenant with admin permissions and multi-tenant with only permissions on one root node
                    this.tenants.push({
                        id: hierarchy.root,
                        name: this.i18n.translate('None__{root}'),
                        children: hierarchy.entities
                    });
                }
                tenants.forEach(res => this.tenants.push({id: res.id, name: res.name, children: res.children}));
                // Create an obj of tenantIds and their respective networks
                this.createTenantsAndNetworks();
                // Set filter and table
                this.searchFilters.selectedTenants.setValue(this.tenants);
                if (this.tenants.length <= 1) {
                    this.displayedColumns = this.displayedColumns.filter(c => c !== 'client');
                }
                if (this.tenants.length === 1) {  // Single tenant or multi-tenant with one permission
                    this.singleTenantId = hierarchy.root || null;
                    // null check avoids cases where root = undefined and the param ends up not being passed
                }
                // this.tenantFilterSubject.pipe(debounceTime(SEARCH_DEBOUNCE_MILLIS))
                //     // map RootNodeInfo[] => string[]
                //     // map(value => (this.form.controls.selectedTenants.value as RootNodeInfo[]).map(rni => rni.id)))
                //     .subscribe(this.filterSubject);
            });
        merge(this.searchForm.controls.filters.valueChanges,
            this.searchForm.controls.textFilter.valueChanges.pipe(
                debounceTime(DEBOUNCE_MILLIS),
            )).pipe(
            takeUntil(this.destroyed)
        ).subscribe(() => {
            this.reloadMembers.next();
        });
    }

    ngAfterViewInit() {
        this.paginator.changes.pipe(
            map((query: QueryList<MatPaginator>) => query?.first),
            startWith(this.paginator.first),
            filter((pag: MatPaginator) => !!pag),
            switchMap(paginator => merge(paginator.page,
                this.reloadMembers.pipe(tap(() => paginator.firstPage()))).pipe(
                startWith(undefined as unknown),
                switchMap(() => this.mkRepresentatives(paginator.pageIndex + 1, paginator.pageSize).pipe(
                    startWith(null as Members), // triggers spinner
                    catchError(err => {
                        this.msgBoxService.error(this.i18n.translate('An error occurred while retrieving subjects'), err);
                        return of(null as Members);
                    }),
                ))
            )),
            takeUntil(this.destroyed)
        ).subscribe(members => {
            this.members.next(members);
            this.expandedMember.next(null);
        });
    }

    ngOnDestroy() {
        this.destroyed.next();
        this.destroyed.complete();
    }

    get searchFilters(): { [ctrl: string]: FormControl } {
        return (this.searchForm.controls.filters as FormGroup).controls as { [ctrl: string]: FormControl };
    }

    get searchTextFilter(): FormControl {
        return this.searchForm.controls.textFilter as FormControl;
    }

    get canCreateRep(): boolean {
        return can(this.hierarchy.permissions, Permission.CREATE);
    }

    get canEditRep(): boolean {
        return can(this.hierarchy.permissions, Permission.UPDATE);
    }

    get canDeleteRep(): boolean {
        return can(this.hierarchy.permissions, Permission.DELETE);
    }

    getSortDirection(criterion: SortCriterion): SortDirection {
        const sortCriterion = this.sortCriteria.find(c => c[0] === criterion);
        return sortCriterion ? sortCriterion[1] : null;
    }

    canFilterTenant(tenant: TenantInfo): boolean {
        const selectedTenant = this.searchFilters.selectedTenants.value as TenantInfo[];
        return !selectedTenant.includes(tenant) || selectedTenant.length > 1;
    }

    isDefined(value: any): boolean {
        return isDefined(value);
    }

    onClickSortBy(criterion: SortCriterion, direction: SortDirection) {
        // remove current matching criterion (regardless of its direction)
        // and add new criterion right on top of criteria list
        this.sortCriteria = [
            [criterion, direction],
            ...this.sortCriteria.filter(c => c[0] !== criterion)
        ];
        // reload members
        this.reloadMembers.next();
    }

    onClickNewRepresentative() {
        this.dialog.open(NewRepresentativeComponent, {
            data: {
                tenantsAndNetworks: this.tenantsAndNetworks,
                tenants: this.tenants,
                singleTenantId: this.singleTenantId
            },
            maxWidth: CHAD_DIALOG_MAX_WIDTH,
            minWidth: CHAD_DIALOG_MIN_WIDTH,
            maxHeight: '95vh',
            hasBackdrop: true
        }).afterClosed()
            .subscribe((newMember: NewRepresentativeMember) => {
                if (newMember) {
                    this.createRepresentative(newMember)
                        .then(() => {
                            // Update tenantsAndNetworks object in case we assigned new comms to the representative
                            this.tenantsAndNetworks = [];
                            this.createTenantsAndNetworks();
                        });
                } else {
                    this.tenantsAndNetworks = [];
                    this.createTenantsAndNetworks();
                }
            });
    }

    convertNewRepresentative(mem: NewRepresentativeMember): NewSecureECRepresentativeRequest {
        return {
            personal_data: mem.personalData,
            tenant: mem.tenant,
            representative_type: mem.memberType,
            password: mem.password,
            network_ids: mem.networkIds,
            enable2fa: mem.enable2fa
        } as NewSecureECRepresentativeRequest;
    }

    onClickEditRepresentative(representative: RepresentativeMember, event: MouseEvent) {
        event.preventDefault();
        event.stopPropagation();
        this.memberToEdit = representative.representative;
        this.editedRepresentativeDialogRef = this.dialogService.open<any, EditedRepresentativeMember>({
            title: this.i18n.translate('Edit representative data'),
            template: this.editRepresentativeDialog,
            context: {
                member: representative.representative,
                community: this.community.value.community
            }
        }, {
            width: DEFAULT_DIALOG_MAX_WIDTH
        });
    }

    onClickSaveEditedRepresentative(editedRepresentative: EditedRepresentativeMember) {
        if (editedRepresentative) {
            this.updateRepresentative(this.memberToEdit, editedRepresentative)
                .then(success => {
                    if (success) {
                        this.editedRepresentativeDialogRef.close(editedRepresentative);
                    }
                });
        }
    }

    onSelectMember(member: RepresentativeMember) {
        this.expandedMember.next(this.expandedMember.value === member ? null : member);
    }

    onSendInvitation(member: ECRepresentative, callback: (member: ECRepresentative) => void) {
        this.inviteRepresentative(member, callback, true);
    }

    onDisableAccount(member: RepresentativeMember) {
        this.dialogService.open<any, boolean>({
            title: this.i18n.translate('Do you want to disable this representative?'),
            template: this.disableRepresentativeDialog,
            context: {
                member
            }
        }).closed.then(push => {
            if (push) {
                this.disableRepresentative(member.representative);
            }
        });
    }

    onClickDeleteRepresentative(member: RepresentativeMember, event: MouseEvent) {
        event.preventDefault();
        event.stopPropagation();
        this.dialogService.open<any, boolean>({
            title: this.i18n.translate('Delete representative'),
            template: this.deleteRepresentativeDialog,
            context: {
                member
            }
        }).closed.then(del => {
            if (del) {
                this.deleteRepresentative(member.representative);
            }
        });
    }

    onReactivateAccount(representative: RepresentativeMember) {
        this.apiService.enableRepresentativeUser(representative.representative.id)
            .then(() => {
                this.msgBoxService.info(this.i18n.translate('Representative re-enabled'));
                return this.mkRepresentative(representative.representative).toPromise();
            })
            .then(updatedRepresentative => this.updateModifiedMember(updatedRepresentative, UpdateReason.USER_ACCOUNT_STATE, representative.representative)) // substitute only updated member
            .catch(err => {
                this.msgBoxService.error(this.i18n.translate('An error occurred while activating the account'), err);
            });
    }

    onForcePwdChange(representative: RepresentativeMember) {
        this.dialogService.open<any, boolean>({
            title: this.i18n.translate('Are you sure you want to force a password change?'),
            template: this.forcePwdChangeDialog,
            context: {
                firstName: representative.representative.personal_data?.first_name || '',
                lastName: representative.representative.personal_data?.last_name || ''
            }
        }).closed.then(change => {
            if (change) {
                this.forcePasswordChange(representative.representative);
            }
        });
    }

    onReset2FA(member: RepresentativeMember) {
        this.dialogService.open<any, boolean>({
            title: this.i18n.translate('Are you sure you want to request re-enabling 2FA?'),
            template: this.force2FAResetDialog,
            context: {
                firstName: member.representative.personal_data?.first_name || '',
                lastName: member.representative.personal_data?.last_name || ''
            }
        }).closed.then(reset => {
            if (reset) {
                this.force2FAReset(member.representative);
            }
        });
    }

    onUpdateTenantsAndNetworks(diff: HierarchyEntity[]) {
        diff.forEach(entry => {
            this.tenantsAndNetworks.forEach(t => {
                if (t.networks.filter(n => n.id === entry.id).length > 0) {
                    if (t.freeNetworks.map(net => net.id).includes(entry.id)) {
                        t.freeNetworks = t.freeNetworks.filter(net => net.id !== entry.id);
                    } else {
                        t.freeNetworks.push(entry);
                    }
                }
            });
        });
    }

    onClickGotoDashboard() {
        this.router.navigate(['/c', 'dev-dashboard'], {
            queryParamsHandling: 'preserve'
        }).then();
    }

    setGap(isEnabled: boolean): string {
        const word = isEnabled ? 'Active' : 'Disabled';
        return (SET_GAP / (this.i18n.translate(word)).length).toFixed(0).toString() + 'px';
    }

    getTenantName(tenantId: string): string {
        return this.tenants.filter(ten => (ten.id === tenantId) || (!ten.id && !tenantId))[0].name;
    }

    resetTenantsFilter() {
        this.searchFilters.selectedTenants.setValue(this.tenants);
    }

    private createRepresentative(newRepresentative: NewRepresentativeMember): Promise<boolean> {
        const spinner = this.openSpinner('We are creating the representative');
        return this.apiService.createRepresentative(this.convertNewRepresentative(newRepresentative))
            .then(resp => {
                if (newRepresentative.sendInvitation) {
                    return this.inviteRepresentative(resp);
                }
            })
            .then(() => {
                spinner.context.next({
                    loading: false,
                    loadingMessage: this.i18n.translate('Representative created')
                });
                this.reloadMembers.next();
                return true;
            })
            .catch(err => {
                const errMsg = this.i18n.translate(mkErrorMessage(err, 'An error occurred while creating the representative'));
                spinner.context.next({
                    loading: false,
                    loadingMessage: errMsg,
                    abort: true
                });
                this.msgBoxService.error(errMsg, err);
                return false;
            });
    }

    private openSpinner(message: string) {
        const spinnerData = new BehaviorSubject<SpinnerData>({
            loading: true,
            loadingMessage: this.i18n.translate(message)
        });
        return this.dialogService.open<Subject<SpinnerData>, undefined>({
            template: this.spinnerDialog,
            context: spinnerData
        }, {
            width: DEFAULT_DIALOG_MIN_WIDTH,
            hideCloseButton: true
        });
    }

    private updateRepresentative(member: ECRepresentative, editedMember: EditedRepresentativeMember): Promise<boolean> {
        const spinner = this.openSpinner('We are updating the subject');
        return this.apiService.updateRepresentative(member.id, mkMemberUpdateRequest(editedMember))
            .then(() => {
                spinner.context.next({
                    loading: false,
                    loadingMessage: this.i18n.translate('Subject updated')
                });
                return this.apiService.loadRepresentative(member.id).toPromise(); // reload member (e.g. if some files have just been uploaded, some new ids may have been generated)
            })
            .then(loadedMember => this.mkRepresentative(loadedMember).toPromise())
            .then(updatedMember => {
                this.updateModifiedMember(updatedMember, UpdateReason.FIELDS, member); // substitute only updated member
                return true;
            })
            .catch(err => {
                const errMsg = this.i18n.translate(mkErrorMessage(err, 'An error occurred while updating representative'));
                spinner.context.next({
                    loading: false,
                    loadingMessage: errMsg,
                    abort: true
                });
                this.msgBoxService.error(errMsg, err);
                return false;
            });
    }

    private deleteRepresentative(representative: ECRepresentative) {
        this.apiService.deleteRepresentative(representative.id)
            .then(() => {
                this.msgBoxService.info(this.i18n.translate('Representative removed'));
                this.reloadMembers.next();
                this.tenantsAndNetworks = [];
                this.createTenantsAndNetworks();
            })
            .catch(err => this.msgBoxService.error(this.i18n.translate('An error occurred while removing representative'), err));
    }

    private inviteRepresentative(member: ECRepresentative, callback?: (member: ECRepresentative) => void, showSpinner?: boolean) {
        const spinner = showSpinner ? this.openSpinner('We are sending the invitation') : null;
        this.apiService.inviteRepresentative(member.id)
            .then(() => {
                if (!showSpinner) {
                    this.msgBoxService.info(this.i18n.translate('Invitation sent successfully'));
                } else {
                    spinner.context.next({
                        loading: false,
                        loadingMessage: this.i18n.translate('Invitation sent successfully')
                    });
                }
                return this.apiService.loadRepresentative(member.id).toPromise();
            })
            .then(mem => {
                if (callback) {
                    callback(mem);
                }
                return this.mkRepresentative(mem).toPromise();
            })
            .then(updatedMember => this.updateModifiedMember(updatedMember, UpdateReason.STATE, member)) // substitute only updated member
            .catch(err => {
                if (showSpinner) {
                    spinner.context.next({
                        loading: false,
                        loadingMessage: this.i18n.translate('Could not send invitation to new member'),
                        abort: true
                    });
                }
                this.msgBoxService.error(this.i18n.translate('Could not send invitation to new member'), err);
            });
    }

    private disableRepresentative(representative: ECRepresentative) {
        this.apiService.disableRepresentativeUser(representative.id)
            .then(() => this.apiService.loadRepresentative(representative.id).toPromise())
            .then(loadedRepresentative => {
                this.msgBoxService.info(this.i18n.translate('Representative disabled'));
                return this.mkRepresentative(loadedRepresentative).toPromise();
            })
            .then(updatedRepresentative => this.updateModifiedMember(updatedRepresentative, UpdateReason.USER_ACCOUNT_STATE, representative)) // substitute only updated member
            .catch(err => {
                this.msgBoxService.error(this.i18n.translate('An error occurred while disabling representative'), err);
            });
    }

    private forcePasswordChange(member: ECRepresentative) {
        this.apiService.forceRepresentativeUserChecks(member.id, MISSING_CHECKS.CHANGE_PASSWORD)
            .then(() => {
                this.msgBoxService.info(this.i18n.translate('Password renewal forced'));
                return this.mkRepresentative(member).toPromise();
            })
            .then(updatedRepresentative => this.updateModifiedMember(updatedRepresentative, UpdateReason.USER_AUTH, member)) // substitute only updated rep
            .catch(err => {
                this.msgBoxService.error(this.i18n.translate('An error occurred while forcing password renewal'), err);
            });
    }

    private force2FAReset(representative: ECRepresentative) {
        this.apiService.forceRepresentativeUserChecks(representative.id, MISSING_CHECKS.ENABLE_TOTP)
            .then(() => {
                this.msgBoxService.info(this.i18n.translate('2FA reset'));
                return this.mkRepresentative(representative).toPromise();
            })
            .then(updatedRepresentative => this.updateModifiedMember(updatedRepresentative, UpdateReason.USER_AUTH, representative)) // substitute only updated rep
            .catch(err => {
                this.msgBoxService.error(this.i18n.translate('An error occurred while resetting 2FA'), err);
            });
    }

    private updateModifiedMember(modifiedMember: RepresentativeMember, reason: UpdateReason, oldMember: ECRepresentative) {
        // check if an overall member reloading is due
        // NOTE: if member reloading is performed, expanded member is cleared as well
        switch (reason) {
            case UpdateReason.USER_ACCOUNT_STATE:
                break;
            case UpdateReason.USER_AUTH:
                break;
            case UpdateReason.FIELDS:
                if (this.needsReloading(oldMember, modifiedMember.representative)) {
                    this.reloadMembers.next();
                    return;
                }
                break;
        }
        // update modified members (no reload needed)
        // const updatedMembers = _.fromPairs(members.map(member => [member.member.id, member]));
        this.members.next({
            members: this.members.value.members.map(m => m.representative.id === modifiedMember.representative.id ? modifiedMember : m),
            total: this.members.value.total
        });
        if (this.expandedMember.value && this.expandedMember.value.representative.id === modifiedMember.representative.id) {
            // update selected member, if it has been modified
            this.expandedMember.next(modifiedMember);
        }
    }

    private mkRepresentatives(page: number, size: number): Observable<Members> {
        // filtering conditions
        const textFilter: string = this.searchTextFilter.value?.trim() || null;
        // tenant filtering and management
        let tenants: TenantInfo[] = [];
        if (this.tenants.length > 0) {
            tenants = this.searchFilters.selectedTenants.value || [];
        } else {  // If no tenants are detected, we only utilize our current one
            // tenants = [this.singleTenant];
            // FIXME
            tenants = null;
        }
        // sorting criteria
        const orderBy: string[] = this.sortCriteria.map(([criterion, direction]) => `${criterion}_${direction}`);
        return this.apiService.listRepresentatives(page, size,
            true,  // OR-search over "text" fields
            textFilter, textFilter, textFilter,  // filter for firstName, lastName, companyName
            textFilter,  // filter for email
            tenants ? tenants.map(ten => ten.id) : null,  // filter for tenant
            orderBy).pipe(
            switchMap(resp => resp.results.length ?
                forkJoin(resp.results.map(member => this.mkRepresentative(member))).pipe(
                    map(members => ({members, total: resp.total} as Members))
                ) :
                of({members: [], total: 0} as Members))
        );
    }

    private mkRepresentative(representative: ECRepresentative): Observable<RepresentativeMember> {
        return forkJoin([
            representative.u3id ? this.apiService.getRepresentativeUser(representative.id) : of(null)
        ]).pipe(
            map(([user]) => ({
                name: this.mkRepresentativeName(representative),
                representative,
                account: user ? {
                    enabled: !user.disabled,
                    mustChangePassword: (user.missing_checks || []).includes(MISSING_CHECKS.CHANGE_PASSWORD),
                    pwExpiresOn: user.password_expires_on,
                    mandatory2fa: (user.missing_checks || []).includes(MISSING_CHECKS.ENABLE_TOTP) || !!user['2fa_enabled'],
                    enabled2fa: !!user['2fa_enabled'],
                    disableReason: user.disable_reason,
                    lastAccessOn: user.last_access_on,
                    disabledOn: user.disabled_on
                } : null
            } as RepresentativeMember))
        );
    }

    private mkRepresentativeName(member: ECRepresentative): string {
        const pd = member.personal_data || {} as ECRepresentativePersonalData;
        if (isBlank(pd.first_name) && isBlank(pd.last_name) && !isBlank(pd.company_name)) {
            return pd.company_name.trim();
        }
        return [pd.first_name, pd.last_name, pd.company_name ? `(${pd.company_name})` : null]
            .filter(n => !isBlank(n))
            .map(n => n.trim())
            .join(' ');
    }

    private needsReloading(oldMember: ECRepresentative, updatedMember: ECRepresentative): boolean {
        if (!!this.searchTextFilter.value?.trim()) {
            if (((oldMember.personal_data.email || null) !== (updatedMember.personal_data.email || null)) ||
                ((oldMember.personal_data.last_name || null) !== (updatedMember.personal_data.last_name || null)) ||
                ((oldMember.personal_data.company_name || null) !== (updatedMember.personal_data.company_name || null))) {
                return true;
            }
        }
        return false;
    }
    private createTenantsAndNetworks() {
        this.tenants.forEach(t => {
            this.tenantsAndNetworks.push({
                tenantId: t.id,
                networks: t.children.filter(child => child.type === EntityType.ENERGY_COMMUNITY)
            });
        });
        // Create subset of networks "free" of a representative
        this.tenantsAndNetworks.forEach(t => {
            t.freeNetworks = t.networks;
            this.apiService.getCommunitiesWithRepresentative(t.tenantId).subscribe(res => {
                if (res.results.length) {
                    res.results.forEach(networkIdWithRep => {
                        t.freeNetworks = t.freeNetworks.filter(x => x.id !== networkIdWithRep);
                    });
                }
            }, err => console.error(err));
        });
    }
}

function mkMemberUpdateRequest(updatedMember: EditedRepresentativeMember): UpdateECRepresentativeRequest {
    return {
        personal_data: updatedMember.personalData,
        tenant: updatedMember.tenant,
        network_ids: updatedMember.networkIds,
        representative_type: updatedMember.representativeType
    };
}

function mkErrorMessage(err: Error, fallback: string): string {
    const code = errorCode(err);
    switch (code) {
        case ErrorCode.REPRESENTATIVE_EMAIL_ALREADY_EXISTING_FOR_USER:
            return 'A user with this email already exists';
        case ErrorCode.REPRESENTATIVE_MOBILE_ALREADY_EXISTING_FOR_USER:
            return 'A user with this mobile number already exists';
        default:
            return fallback;
    }
}

